import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldQuestion, Clock, Undo2, Ban } from 'lucide-react';

import { DataTable, type Column } from '@/components/common/DataTable';
import { PageHeader } from '@/components/common/PageHeader';
import { KpiCard } from '@/components/common/KpiCard';
import { ErrorState } from '@/components/common/ErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cancelsService } from '@/services/cancels';
import { returnsService } from '@/services/returns';
import type { CancelRequest } from '@/types/cancels';
import type { ReturnOrder } from '@/types/returns';
import { fmt } from '@/lib/format';
import { CANCEL_STATUS_LABEL, CANCEL_STATUS_TONE } from './CancelRequestsPage';
import { CancelRequestDetailSheet } from './CancelRequestDetailSheet';
import { ReturnRequestDetailSheet } from '../returns/ReturnRequestDetailSheet';

type Kind = 'cancel' | 'return';

interface ReviewRow {
  kind: Kind;
  id: string;
  number: string;
  externalRef: string | null;
  assignedAt: string | null;
  createdAt: string;
  orderNumber: string;
  customerName: string;
  customerCode: string;
  cancel?: CancelRequest;
  ret?: ReturnOrder;
}

/**
 * Personal review queue — cancel + return requests assigned to the
 * current user awaiting decision. One unified table, filterable by
 * kind. Mirrors the inbox UX in support tools.
 */
export function MyReviewsPage() {
  const [selectedCancel, setSelectedCancel] = useState<CancelRequest | null>(null);
  const [selectedReturn, setSelectedReturn] = useState<ReturnOrder | null>(null);
  const [kindFilter, setKindFilter] = useState<'all' | Kind>('all');

  const cancelsQuery = useQuery({
    queryKey: ['cancels', 'my-reviews'],
    queryFn: () => cancelsService.myReviews(),
    refetchInterval: 30_000,
  });
  const returnsQuery = useQuery({
    queryKey: ['returns', 'my-reviews'],
    queryFn: () => returnsService.myReviews(),
    refetchInterval: 30_000,
  });

  const isLoading = cancelsQuery.isLoading || returnsQuery.isLoading;
  const error = cancelsQuery.error ?? returnsQuery.error;

  const rows: ReviewRow[] = useMemo(() => {
    const out: ReviewRow[] = [];
    for (const c of cancelsQuery.data ?? []) {
      out.push({
        kind: 'cancel',
        id: c.id,
        number: c.cancelNumber,
        externalRef: c.externalRef,
        assignedAt: c.assignedAt ?? null,
        createdAt: c.createdAt,
        orderNumber: c.salesOrder.orderNumber,
        customerName: c.customer.name,
        customerCode: c.customer.code,
        cancel: c,
      });
    }
    for (const r of returnsQuery.data ?? []) {
      out.push({
        kind: 'return',
        id: r.id,
        number: r.returnNumber,
        externalRef: r.externalReturnRef ?? null,
        assignedAt: r.assignedAt ?? null,
        createdAt: r.createdAt,
        orderNumber: r.salesOrder.orderNumber,
        customerName: r.customer?.name ?? '—',
        customerCode: r.customer?.code ?? '—',
        ret: r,
      });
    }
    // Oldest waiting first — most-stale review goes to the top so the
    // SLA-conscious reviewer picks it up first.
    out.sort((a, b) => {
      const ta = a.assignedAt ?? a.createdAt;
      const tb = b.assignedAt ?? b.createdAt;
      return ta < tb ? -1 : ta > tb ? 1 : 0;
    });
    return out;
  }, [cancelsQuery.data, returnsQuery.data]);

  const visible = useMemo(
    () => (kindFilter === 'all' ? rows : rows.filter((r) => r.kind === kindFilter)),
    [rows, kindFilter],
  );

  const summary = useMemo(() => {
    let oldest: string | null = null;
    for (const r of rows) {
      const ts = r.assignedAt ?? r.createdAt;
      if (!oldest || ts < oldest) oldest = ts;
    }
    return {
      pending: rows.length,
      cancels: rows.filter((r) => r.kind === 'cancel').length,
      returns: rows.filter((r) => r.kind === 'return').length,
      oldest,
    };
  }, [rows]);

  const columns: Column<ReviewRow>[] = [
    {
      key: 'kind',
      header: 'Kind',
      width: '100px',
      cell: (r) =>
        r.kind === 'cancel' ? (
          <Badge variant="warning" className="gap-1 text-[10px]">
            <Ban className="h-3 w-3" /> Cancel
          </Badge>
        ) : (
          <Badge variant="info" className="gap-1 text-[10px]">
            <Undo2 className="h-3 w-3" /> Return
          </Badge>
        ),
    },
    {
      key: 'number',
      header: '#',
      width: '170px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium">{r.number}</div>
          {r.externalRef && (
            <div className="font-mono text-[10.5px] text-muted-foreground">{r.externalRef}</div>
          )}
        </div>
      ),
    },
    {
      key: 'assignedAt',
      header: 'Assigned at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {r.assignedAt ? fmt.dateTime(r.assignedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'waiting',
      header: 'Waiting',
      width: '120px',
      cell: (r) => (
        <span className="text-[11.5px] text-amber-600">
          {fmt.relative(r.assignedAt ?? r.createdAt)}
        </span>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      width: '170px',
      cell: (r) => <span className="font-mono text-[12px]">{r.orderNumber}</span>,
    },
    {
      key: 'customer',
      header: 'Customer',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-medium">{r.customerName}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.customerCode}</div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '90px',
      cell: (r) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            if (r.kind === 'cancel' && r.cancel) setSelectedCancel(r.cancel);
            if (r.kind === 'return' && r.ret) setSelectedReturn(r.ret);
          }}
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Workspace"
        title="My Reviews"
        description="Cancel and return requests assigned to you, oldest first. Click a row to approve or reject."
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Pending decisions"
          value={fmt.number(summary.pending)}
          tone={summary.pending ? 'warning' : 'default'}
          icon={ShieldQuestion}
        />
        <KpiCard label="Cancel requests" value={fmt.number(summary.cancels)} tone="info" icon={Ban} />
        <KpiCard label="Return requests" value={fmt.number(summary.returns)} tone="info" icon={Undo2} />
        <KpiCard
          label="Oldest waiting"
          value={summary.oldest ? fmt.relative(summary.oldest) : '—'}
          tone="info"
          icon={Clock}
        />
      </div>

      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant={kindFilter === 'all' ? 'default' : 'outline'}
          onClick={() => setKindFilter('all')}
        >
          All ({summary.pending})
        </Button>
        <Button
          size="sm"
          variant={kindFilter === 'cancel' ? 'default' : 'outline'}
          onClick={() => setKindFilter('cancel')}
        >
          Cancels ({summary.cancels})
        </Button>
        <Button
          size="sm"
          variant={kindFilter === 'return' ? 'default' : 'outline'}
          onClick={() => setKindFilter('return')}
        >
          Returns ({summary.returns})
        </Button>
      </div>

      {error ? (
        <ErrorState
          error={error}
          onRetry={() => {
            cancelsQuery.refetch();
            returnsQuery.refetch();
          }}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={visible}
          rowKey={(r) => `${r.kind}-${r.id}`}
          isLoading={isLoading}
          onRowClick={(r) => {
            if (r.kind === 'cancel' && r.cancel) setSelectedCancel(r.cancel);
            if (r.kind === 'return' && r.ret) setSelectedReturn(r.ret);
          }}
          density="compact"
          emptyTitle="No reviews assigned to you"
          emptyDescription="When a cancel or return request is assigned to your user, it shows up here for approval."
        />
      )}

      <CancelRequestDetailSheet
        cancelRequest={selectedCancel}
        open={!!selectedCancel}
        onOpenChange={(o) => !o && setSelectedCancel(null)}
      />
      <ReturnRequestDetailSheet
        returnRequest={selectedReturn}
        open={!!selectedReturn}
        onOpenChange={(o) => !o && setSelectedReturn(null)}
      />
    </div>
  );
}

// Re-exports used elsewhere — keep import paths from earlier slice working.
export { CANCEL_STATUS_LABEL, CANCEL_STATUS_TONE };
